import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { EmailProvider } from "@/types/inboxPlacements.types";
import { motion } from "framer-motion";
import { Globe, Mail } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useGetAllowedEspTypes } from "@/hooks/use-subscription";

export interface Provider {
  id: string;
  name: string;
  value: EmailProvider;
  icon: string | React.ReactNode;
  type: "b2b" | "b2c";
  description?: string;
}

interface ProviderSectionProps {
  title: string;
  type: "b2b" | "b2c";
  providers: Provider[];
  selectedProviders: string[];
  onSelect: (provider: Provider) => void;
  onSelectAll: (type: "b2b" | "b2c") => void;
}

interface EmailProviderSelectionProps {
  onProvidersChange: (providers: Provider[]) => void;
  initialProviders?: Provider[];
  className?: string;
  showOnlyType?: "b2b" | "b2c";
  testTypeRecipientRef?: "b2b" | "b2c";
  onProviderTypeChange?: (type: "b2b" | "b2c") => void;
  disabled?: boolean;
  allowedEspData?: {
    organizationId: string;
    allowedEspTypes: string[];
    allEspAllowed: boolean;
  } | null;
}

// Mapping from API ESP type strings to EmailProvider enum values
const espTypeToEmailProviderMap: Record<string, EmailProvider> = {
  gmail: EmailProvider.GMAIL,
  outlook: EmailProvider.OUTLOOK,
  aol: EmailProvider.AOL,
  icloud: EmailProvider.ICLOUD,
  gmx: EmailProvider.GMX,
  microsoft365: EmailProvider.MICROSOFT365,
  googleworkspace: EmailProvider.GOOGLEWORKSPACE,
  zoho: EmailProvider.ZOHO,
  workmail: EmailProvider.WORKMAIL,
  rspamd: EmailProvider.RSPAMD,
  abusix: EmailProvider.AMAZONABUSEX,
  spamhaus: EmailProvider.AMAZONSPAMHAUS,
  trendmicro: EmailProvider.AMAZONTRENDMICRO,
  azure: EmailProvider.AZURE,
};

const b2bProviders: Provider[] = [
  {
    id: "google-workspace",
    name: "Google Workspace",
    value: EmailProvider.GOOGLEWORKSPACE,
    icon: (
      <img
        src="/providers-logos/google-workspace.png"
        alt="Google Workspace"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "For business emails hosted on Google Workspace",
  },
  {
    id: "microsoft-365",
    name: "Microsoft 365",
    value: EmailProvider.MICROSOFT365,
    icon: (
      <img
        src="/providers-logos/microsoft-365.png"
        alt="Microsoft 365"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "For business emails hosted on Microsoft 365",
  },
  {
    id: "zoho-workmail",
    name: "Zoho Workmail",
    value: EmailProvider.ZOHO,
    icon: (
      <img
        src="/providers-logos/zoho-mail.png"
        alt="Zoho Workmail"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "For business emails hosted on Zoho Workmail",
  },
  {
    id: "amazon-workmail",
    name: "Amazon Workmail",
    value: EmailProvider.WORKMAIL,
    icon: (
      <img
        src="/providers-logos/aws-workmail.png"
        alt="Amazon Workmail"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "For business emails hosted on Amazon Workmail",
  },
  {
    id: "abusix",
    name: "Abusix",
    value: EmailProvider.AMAZONABUSEX,
    icon: (
      <img
        src="/providers-logos/abusix.png"
        alt="Abusix"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "Enterprise Advanced Spam Filtering System",
  },
  {
    id: "spamhaus",
    name: "Spamhaus",
    value: EmailProvider.AMAZONSPAMHAUS,
    icon: (
      <img
        src="/providers-logos/spamhaus.png"
        alt="Spamhaus"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "Enterprise Advanced Spam Filtering System",
  },
  {
    id: "trend-micro",
    name: "Trend Micro",
    value: EmailProvider.AMAZONTRENDMICRO,
    icon: (
      <img
        src="/providers-logos/trend-micro.png"
        alt="Trend Micro"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "Enterprise Advanced Virus Scanning",
  },
  {
    id: "rspamd",
    name: "Rspamd",
    value: EmailProvider.RSPAMD,
    icon: (
      <img
        src="/providers-logos/rspamd.png"
        alt="Rspamd"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2b",
    description: "Enterprise OpenSource Advanced Spam Filtering System",
  },
];

const b2cProviders: Provider[] = [
  {
    id: "gmail",
    name: "Gmail",
    value: EmailProvider.GMAIL,
    icon: (
      <img
        src="/providers-logos/gmail.png"
        alt="Gmail"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2c",
    description: "For personal Gmail accounts",
  },
  {
    id: "outlook",
    name: "Outlook",
    value: EmailProvider.OUTLOOK,
    icon: (
      <img
        src="/providers-logos/outlook.png"
        alt="Outlook"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2c",
    description: "For personal Outlook.com accounts",
  },
  // {
  //   id: "yahoo",
  //   name: "Yahoo",
  //   icon: (
  //     <img src="/providers-logos/yahoo.png" alt="Yahoo" className="w-5 h-5 object-contain" />
  //   ),
  //   type: "b2c",
  //   description: "For personal Yahoo Mail accounts"
  // },
  {
    id: "aol",
    name: "AOL Mail",
    value: EmailProvider.AOL,
    icon: (
      <img
        src="/providers-logos/aol.png"
        alt="AOL Mail"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2c",
    description: "For personal AOL Mail accounts",
  },
  {
    id: "icloud",
    name: "iCloud Mail",
    value: EmailProvider.ICLOUD,
    icon: (
      <img
        src="/providers-logos/icloud.png"
        alt="iCloud Mail"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2c",
    description: "For personal iCloud Mail accounts",
  },
  {
    id: "gmx",
    name: "GMX",
    value: EmailProvider.GMX,
    icon: (
      <img
        src="/providers-logos/gmx.png"
        alt="GMX"
        className="w-5 h-5 object-contain"
      />
    ),
    type: "b2c",
    description: "For personal GMX Mail accounts",
  },
];

export function EmailProviderSelection({
  onProvidersChange,
  initialProviders = [],
  className,
  showOnlyType,
  onProviderTypeChange,
  testTypeRecipientRef = "b2b",
  disabled = false,
  allowedEspData: allowedEspDataProp,
}: EmailProviderSelectionProps) {
  // Use prop if provided, otherwise fetch from API (for backward compatibility)
  const { data: allowedEspDataFromApi, isLoading: isLoadingAllowedEsp } = useGetAllowedEspTypes();
  const allowedEspData = allowedEspDataProp !== undefined ? allowedEspDataProp : allowedEspDataFromApi;
  
  // If prop is explicitly passed but is null/undefined, we're still loading
  const isLoading = allowedEspDataProp !== undefined ? allowedEspData === null : isLoadingAllowedEsp;

  // Map API ESP types to EmailProvider enum values
  const allowedEmailProviders = useMemo(() => {
    if (!allowedEspData) return null;
    
    // If all ESPs are allowed or allowedEspTypes is empty, return null (show all)
    if (allowedEspData.allEspAllowed || allowedEspData.allowedEspTypes.length === 0) {
      return null;
    }

    // Map API ESP types to EmailProvider enum values
    return new Set(
      allowedEspData.allowedEspTypes
        .map((espType) => espTypeToEmailProviderMap[espType.toLowerCase()])
        .filter((provider): provider is EmailProvider => provider !== undefined)
    );
  }, [allowedEspData]);

  // Filter providers based on allowed ESP types
  const filteredB2BProviders = useMemo(() => {
    if (!allowedEmailProviders) return b2bProviders;
    return b2bProviders.filter((provider) =>
      allowedEmailProviders.has(provider.value)
    );
  }, [allowedEmailProviders]);

  const filteredB2CProviders = useMemo(() => {
    if (!allowedEmailProviders) return b2cProviders;
    return b2cProviders.filter((provider) =>
      allowedEmailProviders.has(provider.value)
    );
  }, [allowedEmailProviders]);

  const allB2BProviderIds = filteredB2BProviders.map((p) => p.id);
  const allB2CProviderIds = filteredB2CProviders.map((p) => p.id);

  // Keep separate selection states for B2B and B2C
  const [b2bSelectedProviders, setB2BSelectedProviders] = useState<string[]>([]);
  const [b2cSelectedProviders, setB2CSelectedProviders] = useState<string[]>([]);

  // Initialize both selection states and notify parent with all selected providers
  useEffect(() => {
    // Initialize selections with all providers of each type
    setB2BSelectedProviders(allB2BProviderIds);
    setB2CSelectedProviders(allB2CProviderIds);

    // Notify parent with all selected providers (both B2B and B2C)
    const allSelectedProviders = [
      ...filteredB2BProviders.filter((p) => allB2BProviderIds.includes(p.id)),
      ...filteredB2CProviders.filter((p) => allB2CProviderIds.includes(p.id)),
    ];
    onProvidersChange(allSelectedProviders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allB2BProviderIds.join(","), allB2CProviderIds.join(",")]);

  const handleProviderSelect = (provider: Provider) => {
    if (disabled) return;

    if (provider.type === "b2b") {
      // Update B2B selections
      setB2BSelectedProviders((prev) => {
        const newSelection = prev.includes(provider.id)
          ? prev.filter((id) => id !== provider.id)
          : [...prev, provider.id];

        // Notify parent with all selected providers (both B2B and B2C)
        const updatedB2BProviders = filteredB2BProviders.filter((p) =>
          newSelection.includes(p.id)
        );
        const updatedB2CProviders = filteredB2CProviders.filter((p) =>
          b2cSelectedProviders.includes(p.id)
        );
        onProvidersChange([...updatedB2BProviders, ...updatedB2CProviders]);

        return newSelection;
      });
    } else {
      // Update B2C selections
      setB2CSelectedProviders((prev) => {
        const newSelection = prev.includes(provider.id)
          ? prev.filter((id) => id !== provider.id)
          : [...prev, provider.id];

        // Notify parent with all selected providers (both B2B and B2C)
        const updatedB2BProviders = filteredB2BProviders.filter((p) =>
          b2bSelectedProviders.includes(p.id)
        );
        const updatedB2CProviders = filteredB2CProviders.filter((p) =>
          newSelection.includes(p.id)
        );
        onProvidersChange([...updatedB2BProviders, ...updatedB2CProviders]);

        return newSelection;
      });
    }
  };

  const handleSelectAll = (type: "b2b" | "b2c") => {
    if (disabled) return;

    const providersOfType = type === "b2b" ? filteredB2BProviders : filteredB2CProviders;
    const providersOfTypeIds = providersOfType.map((p) => p.id);

    if (type === "b2b") {
      setB2BSelectedProviders((prev) => {
        // Check if all of this type are already selected
        const allSelected = providersOfTypeIds.every((id) => prev.includes(id));

        const newSelection = allSelected ? [] : providersOfTypeIds;

        // Update parent with all selected providers
        const updatedB2BProviders = filteredB2BProviders.filter((p) =>
          newSelection.includes(p.id)
        );
        const updatedB2CProviders = filteredB2CProviders.filter((p) =>
          b2cSelectedProviders.includes(p.id)
        );
        onProvidersChange([...updatedB2BProviders, ...updatedB2CProviders]);

        return newSelection;
      });
    } else {
      setB2CSelectedProviders((prev) => {
        // Check if all of this type are already selected
        const allSelected = providersOfTypeIds.every((id) => prev.includes(id));

        const newSelection = allSelected ? [] : providersOfTypeIds;

        // Update parent with all selected providers
        const updatedB2BProviders = filteredB2BProviders.filter((p) =>
          b2bSelectedProviders.includes(p.id)
        );
        const updatedB2CProviders = filteredB2CProviders.filter((p) =>
          newSelection.includes(p.id)
        );
        onProvidersChange([...updatedB2BProviders, ...updatedB2CProviders]);

        return newSelection;
      });
    }
  };

  useEffect(() => {
    // Call onProvidersChange with initial selection if any
    if (initialProviders.length > 0) {
      onProvidersChange(initialProviders);
    }
  }, []);

  const totalSelected = b2bSelectedProviders.length + b2cSelectedProviders.length;
  const totalAvailable = filteredB2BProviders.length + filteredB2CProviders.length;

  // Don't render until allowedEspData is loaded (if it's being fetched)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">Loading providers...</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "space-y-6",
        className,
        disabled ? "opacity-50 pointer-events-none" : ""
      )}
    >
      {/* Header with selection stats */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {totalSelected} of {totalAvailable} ESP's selected
        </span>
      </div>

      {/* Providers sections - Show both B2B and B2C */}
      {filteredB2BProviders.length > 0 && (
        <ProviderSection
          title="B2B Providers"
          type="b2b"
          providers={filteredB2BProviders}
          selectedProviders={b2bSelectedProviders}
          onSelect={handleProviderSelect}
          onSelectAll={handleSelectAll}
        />
      )}

      {filteredB2CProviders.length > 0 && (
        <ProviderSection
          title="B2C Providers"
          type="b2c"
          providers={filteredB2CProviders}
          selectedProviders={b2cSelectedProviders}
          onSelect={handleProviderSelect}
          onSelectAll={handleSelectAll}
        />
      )}
    </div>
  );
}

function ProviderSection({
  title,
  type,
  providers,
  selectedProviders,
  onSelect,
  onSelectAll,
}: ProviderSectionProps) {
  const allSelected = providers.every((provider) =>
    selectedProviders.includes(provider.id)
  );

  const someSelected = providers.some((provider) =>
    selectedProviders.includes(provider.id)
  );

  return (
    <motion.div
      className="space-y-3"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary">
            {type === "b2b" ? (
              <Globe className="w-3.5 h-3.5 text-muted-foreground" />
            ) : (
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
            )}
          </div>
          <h3 className="text-base font-medium flex items-center gap-2">
            {title}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="flex items-center gap-1 font-normal"
          >
            <Mail className="size-3.5 shrink-0" />
            <span>
              {selectedProviders.length * 5} of {providers.length * 5} seed
              accounts
            </span>
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectAll(type)}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>
                {allSelected ? "Deselect all" : "Select all"}{" "}
                {type.toUpperCase()} providers
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {providers.map((provider) => {
          // console.log(selectedProviders, "selectedProviders");
          const isSelected = selectedProviders.includes(provider.id);

          return (
            <Tooltip key={provider.id}>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelect(provider)}
                  className={cn(
                    "shadow-none",
                    // "flex items-center gap-2 px-4 py-2 rounded-md border transition-colors relative group",
                    isSelected
                      ? "bg-primary/10 border-primary/20 text-primary hover:bg-primary/15"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {typeof provider.icon === "string" ? (
                    <img
                      src={provider.icon}
                      alt={provider.name}
                      className="w-5 h-5 object-contain"
                    />
                  ) : (
                    provider.icon
                  )}
                  <span className="text-sm">{provider.name}</span>
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 text-muted-foreground"
                  >
                    <Mail className="!size-3.5" />
                    <span className="text-xs">5</span>
                  </Badge>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-black text-white">
                <p>{provider.description || provider.name}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </motion.div>
  );
}
